import { Component } from '@theme/component';

/**
 * Class set on <html> while a toast is on screen. Sections that have to make room for the toast
 * (the hero copy in new-carousel-banner on mobile, for one) key off this, so their spacing
 * follows along the moment a visitor closes the toast.
 */
const ROOT_VISIBLE_CLASS = 'pb-toast-visible';

/**
 * Toast announcement pill.
 *
 * Renders as a small collapsed pill showing only the announcement image. Clicking the pill expands
 * it to reveal the message; the close button dismisses it (optionally remembered per visitor).
 * Slides are server rendered, this component only swaps which one is visible.
 *
 * Rotation pauses while the toast is expanded, hovered, focused, or while the tab is hidden.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} toggle - The button wrapping the thumbnails.
 * @property {HTMLElement} panel - The message panel.
 * @property {HTMLElement[]} [thumbs] - One wrapper per slide image.
 * @property {HTMLElement[]} [messages] - One element per slide message.
 *
 * @extends {Component<Refs>}
 */
export class PbToastAnnouncement extends Component {
  requiredRefs = ['toggle', 'panel'];

  /** Milliseconds between slides when no valid `data-interval` is set. */
  static defaultInterval = 5000;

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #timer;

  #index = 0;

  connectedCallback() {
    super.connectedCallback();

    if (this.#isDismissed()) {
      this.hidden = true;
      syncRootVisibleClass();
      return;
    }

    this.#show(0);
    this.play();
    syncRootVisibleClass();

    if (window.Shopify?.designMode) {
      this.addEventListener('shopify:block:select', this.#handleBlockSelect);
      this.addEventListener('shopify:block:deselect', this.#handleBlockDeselect);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.pause();
    this.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    this.removeEventListener('shopify:block:deselect', this.#handleBlockDeselect);
    syncRootVisibleClass();
  }

  /** @returns {boolean} Whether the message panel is open. */
  get expanded() {
    return this.dataset.expanded === 'true';
  }

  set expanded(value) {
    this.dataset.expanded = String(value);
    this.refs.toggle.setAttribute('aria-expanded', String(value));

    // Rotating a message the visitor is reading is hostile, so hold on the current slide while open.
    if (value) {
      this.pause();
    } else {
      this.play();
    }
  }

  /** Expands or collapses the message panel. Bound from the markup with `on:click`. */
  toggle() {
    this.expanded = !this.expanded;
  }

  /** Hides the toast for this visitor, and remembers it when `data-remember-days` is set. */
  dismiss() {
    this.pause();
    this.hidden = true;
    syncRootVisibleClass();

    const key = this.#storageKey;
    const days = Number(this.dataset.rememberDays);

    if (!key || !Number.isFinite(days) || days <= 0) return;

    try {
      localStorage.setItem(key, String(Date.now() + days * 24 * 60 * 60 * 1000));
    } catch {
      // Private mode or a full quota — dismissing for this page view is good enough.
    }
  }

  /** Starts rotating through the slides. */
  play() {
    this.pause();

    const count = this.#slideCount;

    if (count < 2 || this.expanded) return;

    this.#timer = setInterval(() => {
      if (document.hidden || this.matches(':hover, :focus-within')) return;

      this.#show(this.#index + 1);
    }, this.#interval);
  }

  /** Stops rotating through the slides. */
  pause() {
    clearInterval(this.#timer);
    this.#timer = undefined;
  }

  get #slideCount() {
    const { thumbs = [], messages = [] } = this.refs;

    return Math.max(thumbs.length, messages.length);
  }

  get #interval() {
    const interval = Number(this.dataset.interval);

    return Number.isFinite(interval) && interval > 0 ? interval : PbToastAnnouncement.defaultInterval;
  }

  get #storageKey() {
    return this.dataset.storageKey || '';
  }

  /**
   * Reveals a single slide and hides the others.
   *
   * @param {number} index - Index of the slide to show, wrapped into range.
   */
  #show(index) {
    const count = this.#slideCount;

    if (count === 0) return;

    this.#index = ((index % count) + count) % count;

    const { thumbs = [], messages = [] } = this.refs;

    // Liquid renders one thumb and one message per slide, so both lists share the same indexes.
    for (const slides of [thumbs, messages]) {
      for (const [position, slide] of slides.entries()) {
        slide.toggleAttribute('data-active', position === this.#index);
      }
    }
  }

  /**
   * Opens the toast on the block a merchant selects in the theme editor.
   *
   * @param {Event} event - The `shopify:block:select` event.
   */
  #handleBlockSelect = (event) => {
    const target = /** @type {Node} */ (event.target);
    const index = (this.refs.messages ?? []).findIndex((message) => message.contains(target));

    if (index === -1) return;

    this.#show(index);
    this.expanded = true;
  };

  #handleBlockDeselect = () => {
    this.expanded = false;
  };

  /** @returns {boolean} Whether this visitor dismissed the toast earlier. */
  #isDismissed() {
    const key = this.#storageKey;
    const remembers = this.dataset.rememberDays != null;

    // Merchants need to see the toast in the theme editor even after dismissing it once.
    if (!key || !remembers || window.Shopify?.designMode) return false;

    try {
      const expiry = Number(localStorage.getItem(key));

      if (!expiry) return false;

      if (expiry > Date.now()) return true;

      localStorage.removeItem(key);
    } catch {
      // Storage unavailable — show the toast.
    }

    return false;
  }
}

/** Keeps the root class in step with whether any toast is currently on screen. */
function syncRootVisibleClass() {
  const visible = document.querySelector('pb-toast-announcement:not([hidden])') != null;

  document.documentElement.classList.toggle(ROOT_VISIBLE_CLASS, visible);
}

if (!customElements.get('pb-toast-announcement')) {
  customElements.define('pb-toast-announcement', PbToastAnnouncement);
}
